import { faDownload, faFolderGear } from "@fortawesome/pro-light-svg-icons";
import {
    faExpand,
    faInfoCircle,
    faMinimize,
} from "@fortawesome/sharp-light-svg-icons";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { useAnalyticsApiClient, useAppSelector } from "../../../hooks";
import { ExpandableArea, useAreaExpansion } from "../../../ui/ExpandableArea";
import { FloatingMenu } from "../../../ui/Menu";
import { Modal } from "../../../ui/Modals";
import type { AnalyticsFilters } from "../state";
import { getQuarterDateRange } from "../utils/quarterUtils";
import { useFilterText } from "../utils/useFilterText";
import { useImageDownload } from "../utils/useImageDownload";
import { TotalPerSiteAdmin } from "./admin";
import { PercentagePlot } from "./percentagePlot";

interface TotalPerSiteProps {
    filters: AnalyticsFilters;
}

export const TotalPerSite = ({ filters }: TotalPerSiteProps) => {
    const analyticsApi = useAnalyticsApiClient();
    const userIsStaff = useAppSelector((state) => state.auth.isStaff);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
    const svgRef = useRef<SVGSVGElement>(null);
    const filterText = useFilterText();
    const { downloadImage } = useImageDownload(
        svgRef,
        "total_per_site",
        filterText,
    );

    // Query 1: Site with emission counts
    const siteQuery = useQuery({
        queryKey: [
            "analyticsSitesWithEmissions",
            filters.quarterFrom,
            filters.quarterTo,
            filters.detectionLimit,
            filters.providers,
            filters.locationPreset,
            filters.siteType,
        ],
        queryFn: async () => {
            const startRange = getQuarterDateRange(filters.quarterFrom);
            const endRange = getQuarterDateRange(filters.quarterTo);

            return await analyticsApi.analyticsSitesList({
                pageSize: 25,
                timestampAfter: startRange.start,
                timestampBefore: endRange.end,
                detectionThreshold: (filters.detectionLimit ?? 100) * 1000,
                providerWithSource:
                    filters.providers === "all" || !filters.providers
                        ? undefined
                        : (JSON.stringify(filters.providers) as any),
                locationPreset: filters.locationPreset?.length
                    ? filters.locationPreset
                    : undefined,
                siteType: filters.siteType?.length
                    ? filters.siteType
                    : undefined,
            });
        },
        refetchOnWindowFocus: false,
        staleTime: 300,
    });

    const { expandedCardId, expandCard, contractCard } = useAreaExpansion();
    const extraActions = useMemo(() => {
        const actions = [
            {
                icon: faInfoCircle,
                title: "Plot information",
                fn: () => setShowInfoModal(true),
            },
            {
                icon: faDownload,
                title: "Download plot as image (JPEG)",
                fn: downloadImage as () => void,
            },
        ];
        if (userIsStaff) {
            actions.push({
                icon: faFolderGear,
                title: "Download data as CSV (admin only)",
                fn: () => setIsDownloadModalOpen(true),
            });
        }

        const isExpanded = expandedCardId === "obs_per_site";
        return [
            ...actions,
            {
                icon: isExpanded ? faMinimize : faExpand,
                title: "Expand plot",
                fn: () =>
                    isExpanded ? contractCard() : expandCard("obs_per_site"),
            },
        ];
    }, [downloadImage, userIsStaff, expandedCardId, contractCard, expandCard]);

    return (
        <>
            <ExpandableArea
                cardId="obs_per_site"
                expandable={true}
                className="h-full relative"
                content={
                    <div className="h-full p-1 group">
                        <FloatingMenu
                            actions={extraActions}
                            className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-400"
                        />
                        <PercentagePlot
                            svgRef={svgRef}
                            siteData={siteQuery.data?.results || []}
                            loading={siteQuery.isLoading}
                            filters={filters}
                        />
                    </div>
                }
            />

            <Modal
                visible={showInfoModal}
                onClose={() => setShowInfoModal(false)}
                size="sm"
            >
                <div className="my-4 text-sm">
                    <h2 className="text-lg font-semibold mb-4">
                        Plot information
                    </h2>
                    <p className="mb-2">
                        This figure shows the percentage of observations
                        exceeding the chosen threshold for your company sites
                        tracked by AERSHED. The sites with the highest
                        percentage of observations over the threshold are on the
                        left side. Only the 25 sites with the highest percentage
                        are displayed.
                    </p>
                    <p className="mb-2">
                        For example, with a 100 kg per hour threshold this
                        figure will show a bar for each site representing how
                        many times, as a ratio of how often it was observed,
                        emissions above 100 kg/h were detected at that site.
                    </p>
                    <p className="mb-2">
                        Since all observations are required, including those
                        with very low or zero measured emissions, this figure
                        only includes data from providers where we can infer
                        non-detects. Data from providers that provide
                        information at the equipment level are aggregated to
                        site level.
                    </p>
                    <p>
                        If only a small number of observations have been made at
                        a site, or if the threshold is set very low, then it is
                        not unusual to see multiple sites with a value of 100%.
                    </p>
                </div>
            </Modal>

            {userIsStaff && (
                <Modal
                    visible={isDownloadModalOpen}
                    onClose={() => setIsDownloadModalOpen(false)}
                    size="sm"
                >
                    <div className="mt-4">
                        <h2 className="text-lg font-semibold mb-4">
                            Download Data
                        </h2>
                        <TotalPerSiteAdmin
                            data={siteQuery.data?.results || []}
                            isLoading={siteQuery.isLoading}
                        />
                    </div>
                </Modal>
            )}
        </>
    );
};
